import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect } from 'react'
import {
  fetchMinimalRegItems,
  FETCH_MINIMAL_REG_ITEMS_KEY,
} from 'src/api/queries'
import { RegItemMinimal } from 'src/types/regItem'

const INITIAL_PAGE_SIZE = 10
const PAGE_SIZE = 50

interface RegItemsMinimalDataPage {
  data: RegItemMinimal[]
  offset: number
  limit: number
}

const useGetRegItemsMinimal = (registryId: number | undefined) => {
  const regItemsMinimalQuery = useInfiniteQuery({
    enabled: !!registryId,
    queryKey: [FETCH_MINIMAL_REG_ITEMS_KEY, registryId],
    queryFn: async ({
      pageParam = { offset: 0, limit: INITIAL_PAGE_SIZE },
    }) => {
      const { offset, limit } = pageParam
      const data = await fetchMinimalRegItems({
        registryId: registryId as number,
        offset,
        limit,
      })

      return { data, offset, limit } as RegItemsMinimalDataPage
    },
    getNextPageParam: (lastPage) => {
      // Returning undefined sets hasNextPage to false and stops making requests.
      if (lastPage.data.length < lastPage.limit) return undefined

      return { offset: lastPage.offset + lastPage.limit, limit: PAGE_SIZE }
    },
  })

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    regItemsMinimalQuery

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  let regItems: RegItemMinimal[] = []
  if (data && data.pages) {
    regItems = (
      data.pages as InfiniteData<RegItemsMinimalDataPage>['pages']
    ).reduce((acc, page) => [...acc, ...page.data], [] as RegItemMinimal[])
  }

  return { regItems, regItemsMinimalQuery }
}

export const useInvalidateRegItemsMinimal = (registryId: number) => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries([FETCH_MINIMAL_REG_ITEMS_KEY, registryId])
}

export const useOptimisticAddRegItemMinimal = () => {
  const queryClient = useQueryClient()

  return (registryId: number, newRegItem: RegItemMinimal) => {
    queryClient.setQueryData(
      [FETCH_MINIMAL_REG_ITEMS_KEY, registryId],
      (currentData?: InfiniteData<RegItemsMinimalDataPage>) => {
        if (!currentData) return undefined

        return {
          ...currentData,
          pages: [
            ...currentData.pages.slice(0, -1),
            {
              ...currentData.pages[currentData.pages.length - 1],
              data: [
                ...currentData.pages[currentData.pages.length - 1].data,
                newRegItem,
              ],
            },
          ],
        }
      }
    )
  }
}

export const useOptimisticDeleteRegItemMinimal = () => {
  const queryClient = useQueryClient()

  return (registryId: number, regItemId: number) => {
    queryClient.setQueryData(
      [FETCH_MINIMAL_REG_ITEMS_KEY, registryId],
      (currentData?: InfiniteData<RegItemsMinimalDataPage>) => {
        if (!currentData) return undefined

        return {
          ...currentData,
          pages: currentData.pages.map((page) => ({
            ...page,
            data: page.data.filter((ri) => ri.id !== regItemId),
          })),
        }
      }
    )
  }
}

export default useGetRegItemsMinimal
